import { toUserMessage } from '../../lib/errors.js';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useEnqueueMaster, useMasters } from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCelebrate } from '../../components/CelebrationToast.js';

interface BusinessRow {
  id: string;
  tenant_id: string;
  name: string;
  ragione_sociale: string | null;
  piva: string | null;
  telefono: string | null;
  address: string | null;
  city: string | null;
  cap: string | null;
  provincia: string | null;
  giorni_chiusura: string[] | null;
  tipologia_attivita: string | null;
  posti_sala: number | null;
  posti_dehor: number | null;
  version: number;
}

interface TenantAnagrafica {
  id: string;
  name: string;
  piva: string | null;
  codice_fiscale: string | null;
  forma_giuridica: string | null;
  rea_number: string | null;
  scia_protocol: string | null;
  scia_date: string | null;
  notifica_sanitaria_ente: string | null;
  notifica_sanitaria_date: string | null;
  manuale_haccp_version: string | null;
  manuale_haccp_revised_at: string | null;
  responsabile_haccp_name: string | null;
}

const GIORNI: { key: string; label: string }[] = [
  { key: 'lun', label: 'Lun' },
  { key: 'mar', label: 'Mar' },
  { key: 'mer', label: 'Mer' },
  { key: 'gio', label: 'Gio' },
  { key: 'ven', label: 'Ven' },
  { key: 'sab', label: 'Sab' },
  { key: 'dom', label: 'Dom' },
];

export function BusinessDetailsPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: businesses = [] } = useMasters<BusinessRow>('businesses');
  const enqueueMaster = useEnqueueMaster();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);
  const celebrate = useCelebrate();

  const tenantId = profile?.active_tenant_id ?? null;
  const { data: tenant } = useQuery<TenantAnagrafica | null>({
    queryKey: ['tenant_anagrafica', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      if (!tenantId) return null;
      const { data, error } = await getSupabase()
        .from('tenants')
        .select('id, name, piva, codice_fiscale, forma_giuridica, rea_number, scia_protocol, scia_date, notifica_sanitaria_ente, notifica_sanitaria_date, manuale_haccp_version, manuale_haccp_revised_at, responsabile_haccp_name')
        .eq('id', tenantId)
        .maybeSingle();
      if (error) throw error;
      return (data as TenantAnagrafica | null) ?? null;
    },
  });

  const current = useMemo(
    () => businesses.find((b) => b.id === profile?.active_business_id) ?? null,
    [businesses, profile?.active_business_id],
  );

  const [form, setForm] = useState({
    // Business fields
    name: '',
    ragione_sociale: '',
    piva: '',
    telefono: '',
    address: '',
    city: '',
    cap: '',
    provincia: '',
    giorni_chiusura: [] as string[],
    tipologia_attivita: '',
    posti_sala: '',
    posti_dehor: '',
    // Tenant fields (anagrafica HACCP)
    tenant_codice_fiscale: '',
    forma_giuridica: '',
    rea_number: '',
    scia_protocol: '',
    scia_date: '',
    notifica_sanitaria_ente: '',
    notifica_sanitaria_date: '',
    manuale_haccp_version: '',
    manuale_haccp_revised_at: '',
    responsabile_haccp_name: '',
  });

  // T-012: pre-fill cross-tabella usando i dati raccolti in fase di registrazione.
  //  - `bootstrap_new_user` raccoglie `tenantName`, `tenantPiva`, `businessName`,
  //    `fullName` e li scrive su `tenants.name`, `tenants.piva`, `businesses.name`,
  //    `user_profiles.full_name`.
  //  - Questa scheda però usa `businesses.ragione_sociale`, `businesses.piva` e
  //    `tenants.responsabile_haccp_name`, che restano vuoti. Senza pre-fill il
  //    ristoratore vede la scheda "vuota" anche se ha appena fornito quei dati.
  //  - Il pre-fill avviene SOLO nel form (no auto-save): il valore canonico
  //    resta dove è oggi finché l'utente non preme "Salva".
  useEffect(() => {
    if (!current) return;
    setForm((f) => ({
      ...f,
      name: current.name ?? '',
      // Fallback business→tenant: se business non ha ragione sociale / piva
      // ma il tenant li ha (caso bootstrap), riempiamo già il campo.
      ragione_sociale: current.ragione_sociale ?? tenant?.name ?? '',
      piva: current.piva ?? tenant?.piva ?? '',
      telefono: current.telefono ?? '',
      address: current.address ?? '',
      city: current.city ?? '',
      cap: current.cap ?? '',
      provincia: current.provincia ?? '',
      giorni_chiusura: current.giorni_chiusura ?? [],
      tipologia_attivita: current.tipologia_attivita ?? '',
      posti_sala: current.posti_sala != null ? String(current.posti_sala) : '',
      posti_dehor: current.posti_dehor != null ? String(current.posti_dehor) : '',
    }));
    // tenant.name/piva sono usati come fallback. Se tenant arriva DOPO current,
    // il secondo useEffect compensa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      tenant_codice_fiscale: tenant?.codice_fiscale ?? '',
      forma_giuridica: tenant?.forma_giuridica ?? '',
      rea_number: tenant?.rea_number ?? '',
      scia_protocol: tenant?.scia_protocol ?? '',
      scia_date: tenant?.scia_date ?? '',
      notifica_sanitaria_ente: tenant?.notifica_sanitaria_ente ?? '',
      notifica_sanitaria_date: tenant?.notifica_sanitaria_date ?? '',
      manuale_haccp_version: tenant?.manuale_haccp_version ?? '',
      manuale_haccp_revised_at: tenant?.manuale_haccp_revised_at ?? '',
      // Pre-fill responsabile HACCP col nome dell'utente registrato.
      responsabile_haccp_name: tenant?.responsabile_haccp_name ?? profile?.full_name ?? '',
      // Compensazione fallback business→tenant se tenant arriva dopo current:
      // sovrascriviamo SOLO se l'utente non ha ancora scritto nulla.
      ragione_sociale: f.ragione_sociale || (tenant?.name ?? ''),
      piva: f.piva || (tenant?.piva ?? ''),
    }));
  }, [tenant, profile?.full_name]);

  function toggleGiorno(key: string): void {
    setForm((f) => {
      const has = f.giorni_chiusura.includes(key);
      return {
        ...f,
        giorni_chiusura: has ? f.giorni_chiusura.filter((k) => k !== key) : [...f.giorni_chiusura, key],
      };
    });
  }

  async function save(): Promise<void> {
    if (!current || !profile?.active_tenant_id) {
      setToast('Nessun locale attivo');
      return;
    }
    const piva = form.piva.trim();
    if (piva && !/^\d{11}$/.test(piva)) {
      setToast('P.IVA deve essere 11 cifre');
      return;
    }
    const cap = form.cap.trim();
    if (cap && !/^\d{5}$/.test(cap)) {
      setToast('CAP deve essere 5 cifre');
      return;
    }
    const provincia = form.provincia.trim().toUpperCase();
    if (provincia && provincia.length !== 2) {
      setToast('Provincia: 2 lettere');
      return;
    }
    // Validazioni anagrafica HACCP (tenant)
    const postiSala = form.posti_sala.trim();
    const postiDehor = form.posti_dehor.trim();
    if (postiSala && !/^\d+$/.test(postiSala)) { setToast('Posti sala: solo numeri'); return; }
    if (postiDehor && !/^\d+$/.test(postiDehor)) { setToast('Posti dehor: solo numeri'); return; }

    try {
      await enqueueMaster.mutateAsync({
        table: 'businesses',
        id: current.id,
        op: 'update',
        base_version: current.version,
        patch: {
          id: current.id,
          tenant_id: profile.active_tenant_id,
          name: form.name.trim(),
          ragione_sociale: form.ragione_sociale.trim() || null,
          piva: piva || null,
          telefono: form.telefono.trim() || null,
          address: form.address.trim() || null,
          city: form.city.trim() || null,
          cap: cap || null,
          provincia: provincia || null,
          giorni_chiusura: form.giorni_chiusura,
          tipologia_attivita: form.tipologia_attivita.trim() || null,
          posti_sala: postiSala ? Number(postiSala) : null,
          posti_dehor: postiDehor ? Number(postiDehor) : null,
        },
      });
      // Aggiornamento anagrafica HACCP direttamente sul tenant (no offline-first:
      // questi campi si configurano una volta, non durante il servizio).
      const { error: tErr } = await getSupabase()
        .from('tenants')
        .update({
          codice_fiscale: form.tenant_codice_fiscale.trim() || null,
          forma_giuridica: form.forma_giuridica.trim() || null,
          rea_number: form.rea_number.trim() || null,
          scia_protocol: form.scia_protocol.trim() || null,
          scia_date: form.scia_date || null,
          notifica_sanitaria_ente: form.notifica_sanitaria_ente.trim() || null,
          notifica_sanitaria_date: form.notifica_sanitaria_date || null,
          manuale_haccp_version: form.manuale_haccp_version.trim() || null,
          manuale_haccp_revised_at: form.manuale_haccp_revised_at || null,
          responsabile_haccp_name: form.responsabile_haccp_name.trim() || null,
        })
        .eq('id', profile.active_tenant_id);
      if (tErr) throw tErr;
      void qc.invalidateQueries({ queryKey: ['tenant_anagrafica', profile.active_tenant_id] });
      celebrate('Dati locale salvati');
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>Dati locale / azienda</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        {!current ? (
          <div className="ion-padding">
            <IonNote>{t('common.empty')}</IonNote>
          </div>
        ) : (
          <>
            <IonList inset>
              <IonListHeader>
                <IonLabel>Identita fiscale</IonLabel>
              </IonListHeader>
              <IonItem>
                <IonLabel position="stacked">Nome del locale *</IonLabel>
                <IonInput
                  value={form.name}
                  onIonInput={(ev) => setForm((f) => ({ ...f, name: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Ragione Sociale</IonLabel>
                <IonInput
                  value={form.ragione_sociale}
                  placeholder="Mario Rossi S.r.l."
                  onIonInput={(ev) => setForm((f) => ({ ...f, ragione_sociale: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">P.IVA</IonLabel>
                <IonInput
                  value={form.piva}
                  inputmode="numeric"
                  maxlength={11}
                  placeholder="11 cifre"
                  className={form.piva && !/^\d{11}$/.test(form.piva) ? 'ion-touched ion-invalid' : ''}
                  errorText="P.IVA deve essere 11 cifre"
                  onIonInput={(ev) => setForm((f) => ({ ...f, piva: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Telefono</IonLabel>
                <IonInput
                  type="tel"
                  value={form.telefono}
                  onIonInput={(ev) => setForm((f) => ({ ...f, telefono: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            </IonList>

            <IonList inset>
              <IonListHeader>
                <IonLabel>Sede</IonLabel>
              </IonListHeader>
              <IonItem>
                <IonLabel position="stacked">Indirizzo</IonLabel>
                <IonInput
                  value={form.address}
                  onIonInput={(ev) => setForm((f) => ({ ...f, address: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Citta</IonLabel>
                <IonInput
                  value={form.city}
                  onIonInput={(ev) => setForm((f) => ({ ...f, city: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">CAP</IonLabel>
                <IonInput
                  value={form.cap}
                  inputmode="numeric"
                  maxlength={5}
                  onIonInput={(ev) => setForm((f) => ({ ...f, cap: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Provincia</IonLabel>
                <IonInput
                  value={form.provincia}
                  maxlength={2}
                  placeholder="MI"
                  onIonInput={(ev) => setForm((f) => ({ ...f, provincia: String(ev.detail.value ?? '').toUpperCase() }))}
                />
              </IonItem>
            </IonList>

            <IonList inset>
              <IonListHeader>
                <IonLabel>Giorni di chiusura</IonLabel>
              </IonListHeader>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: 12 }}>
                {GIORNI.map((g) => {
                  const selected = form.giorni_chiusura.includes(g.key);
                  return (
                    <IonChip
                      key={g.key}
                      color={selected ? 'primary' : 'medium'}
                      onClick={() => toggleGiorno(g.key)}
                    >
                      <IonLabel>{g.label}</IonLabel>
                    </IonChip>
                  );
                })}
              </div>
            </IonList>

            <IonList inset>
              <IonListHeader>
                <IonLabel>Tipologia attivita</IonLabel>
              </IonListHeader>
              <IonItem>
                <IonLabel position="stacked">Tipo (es. Ristorazione tradizionale, Bar, Tavola calda)</IonLabel>
                <IonInput
                  value={form.tipologia_attivita}
                  placeholder="Ristorazione tradizionale"
                  onIonInput={(ev) => setForm((f) => ({ ...f, tipologia_attivita: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Posti a sedere - sala interna</IonLabel>
                <IonInput
                  value={form.posti_sala}
                  inputmode="numeric"
                  placeholder="0"
                  onIonInput={(ev) => setForm((f) => ({ ...f, posti_sala: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Posti a sedere - dehor / esterno</IonLabel>
                <IonInput
                  value={form.posti_dehor}
                  inputmode="numeric"
                  placeholder="0"
                  onIonInput={(ev) => setForm((f) => ({ ...f, posti_dehor: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            </IonList>

            <IonList inset>
              <IonListHeader>
                <IonLabel>Conformita HACCP (Report ASL)</IonLabel>
              </IonListHeader>
              <div style={{ padding: '0 16px 4px', fontSize: 12, color: '#64748b' }}>
                Campi che compaiono nella Sezione 0 del Report ASL. Se vuoti, il PDF
                stampa "Non configurato".
              </div>
              <IonItem>
                <IonLabel position="stacked">Codice Fiscale azienda</IonLabel>
                <IonInput
                  value={form.tenant_codice_fiscale}
                  placeholder="RSSMRA80A01H501Z o 02145678901"
                  onIonInput={(ev) => setForm((f) => ({ ...f, tenant_codice_fiscale: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Forma giuridica</IonLabel>
                <IonInput
                  value={form.forma_giuridica}
                  placeholder="Ditta individuale / S.r.l. / S.n.c. / ..."
                  onIonInput={(ev) => setForm((f) => ({ ...f, forma_giuridica: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Numero REA (Camera di Commercio)</IonLabel>
                <IonInput
                  value={form.rea_number}
                  placeholder="AG-12345"
                  onIonInput={(ev) => setForm((f) => ({ ...f, rea_number: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Responsabile HACCP (nome)</IonLabel>
                <IonInput
                  value={form.responsabile_haccp_name}
                  placeholder="Mario Rossi"
                  onIonInput={(ev) => setForm((f) => ({ ...f, responsabile_haccp_name: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">SCIA - protocollo SUAP</IonLabel>
                <IonInput
                  value={form.scia_protocol}
                  placeholder="2018/4521"
                  onIonInput={(ev) => setForm((f) => ({ ...f, scia_protocol: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">SCIA - data di presentazione</IonLabel>
                <IonInput
                  type="date"
                  value={form.scia_date}
                  onIonInput={(ev) => setForm((f) => ({ ...f, scia_date: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Notifica sanitaria - ente rilasciante</IonLabel>
                <IonInput
                  value={form.notifica_sanitaria_ente}
                  placeholder="ASP Agrigento"
                  onIonInput={(ev) => setForm((f) => ({ ...f, notifica_sanitaria_ente: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Notifica sanitaria - data rilascio</IonLabel>
                <IonInput
                  type="date"
                  value={form.notifica_sanitaria_date}
                  onIonInput={(ev) => setForm((f) => ({ ...f, notifica_sanitaria_date: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Manuale HACCP - versione</IonLabel>
                <IonInput
                  value={form.manuale_haccp_version}
                  placeholder="3.2"
                  onIonInput={(ev) => setForm((f) => ({ ...f, manuale_haccp_version: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Manuale HACCP - ultima revisione</IonLabel>
                <IonInput
                  type="date"
                  value={form.manuale_haccp_revised_at}
                  onIonInput={(ev) => setForm((f) => ({ ...f, manuale_haccp_revised_at: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            </IonList>

            <div className="ion-padding">
              <IonButton expand="block" onClick={() => void save()}>
                Salva
              </IonButton>
            </div>
          </>
        )}

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={1800}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
